/**
 * Reglas puras de la ficha: mínimo Omnibus y detección de cambios locales.
 */

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

export interface PricePoint {
    precio: number;
    recordedAt: string;
}

export interface ContentDraft {
    nombre: string;
    editorial: string;
    veredicto: string;
    descripcionCorta: string;
    descripcionLarga: string;
    seoTitle: string;
    seoDescription: string;
    imagenAlt: string;
    badges: string;
    pescaTags: string;
    especies: string;
    guias: string;
    qualityStatus: string;
    shippingPackageType: string;
    shippingPackageLoaded: string;
    devolucionRecogidaSinCoste: boolean;
    faq: Array<{ q: string; a: string }>;
    specs: Array<{ key: string; value: string }>;
    categoriaRuta: string;
    coleccionSlugs: string;
}

export interface MerchVariantDraft {
    id: string;
    priceEur: string;
    stock: string;
    stripePriceId: string;
    isActive: boolean;
}

export interface MerchDraft {
    name: string;
    description: string;
    category: string;
    sortOrder: string;
    variants: MerchVariantDraft[];
}

/**
 * Precio mínimo vigente en los últimos 30 días.
 * El último registro anterior a la ventana sigue en vigor al empezar el periodo.
 * Sin historial, se usa el precio actual.
 */
export function omnibusMinPrice(
    history: PricePoint[],
    now: Date,
    currentPrice?: number | null,
): number | null {
    const nowMs = now.getTime();
    const start = nowMs - THIRTY_DAYS_MS;
    const points = history
        .map((row) => ({ at: new Date(row.recordedAt).getTime(), precio: Number(row.precio) }))
        .filter((row) => Number.isFinite(row.at) && Number.isFinite(row.precio))
        .sort((a, b) => a.at - b.at);
    const before = [...points].reverse().find((row) => row.at < start);
    const inside = points.filter((row) => row.at >= start && row.at <= nowMs);
    const prices = [
        ...(before ? [before.precio] : []),
        ...inside.map((row) => row.precio),
    ];
    if (currentPrice != null && Number.isFinite(Number(currentPrice))) {
        prices.push(Number(currentPrice));
    }
    if (prices.length === 0) return null;
    return Math.min(...prices);
}

/** Marca que ve el cliente: la editorial si existe, si no la del fabricante. */
export function publishedBrand(detail: {
    marcaEditorial?: string | null;
    marcaProveedor?: string | null;
    marca?: string | null;
}): string {
    const editorial = (detail.marcaEditorial ?? '').trim();
    if (editorial) return editorial;
    return (detail.marcaProveedor || detail.marca || '').trim();
}

export function contentDraftSnapshot(draft: ContentDraft): string {
    return JSON.stringify({
        ...draft,
        faq: draft.faq.map((item) => ({ q: item.q, a: item.a })),
        specs: draft.specs.map((row) => ({ key: row.key, value: row.value })),
    });
}

export function contentDraftIsDirty(saved: string, current: ContentDraft): boolean {
    return saved !== contentDraftSnapshot(current);
}

export function merchDraftSnapshot(draft: MerchDraft): string {
    return JSON.stringify({
        ...draft,
        variants: [...draft.variants].sort((a, b) => a.id.localeCompare(b.id)),
    });
}

export function merchDraftIsDirty(saved: string, current: MerchDraft): boolean {
    return saved !== merchDraftSnapshot(current);
}

/** Guardar el producto no confirma las variantes que aún no se han guardado. */
export function merchBaselineAfterProductSave(saved: string, current: MerchDraft): string {
    const previous = JSON.parse(saved) as MerchDraft;
    return merchDraftSnapshot({ ...current, variants: previous.variants });
}

/** Guardar una variante no confirma el resto del formulario. */
export function merchBaselineAfterVariantSave(saved: string, variant: MerchVariantDraft): string {
    const previous = JSON.parse(saved) as MerchDraft;
    return merchDraftSnapshot({
        ...previous,
        variants: previous.variants.map((item) => (item.id === variant.id ? variant : item)),
    });
}
